(() => {
  if (window.__bbProductPageInitialized) return;
  window.__bbProductPageInitialized = true;

  const SOURCE = 'bb-product-cart-control';
  let cartSubscribed = false;
  let lastExternalSyncAt = 0;

  function getProductInfos(scope = document) {
    if (scope?.matches?.('product-info')) return [scope];
    return Array.from(scope?.querySelectorAll?.('product-info') || []);
  }

  function getVariantId(productInfo) {
    return (
      productInfo.querySelector(
        'form[data-type="add-to-cart-form"] input[name="id"], form[action*="/cart/add"] input[name="id"]'
      )?.value || ''
    );
  }

  function getQuantityInput(productInfo) {
    return productInfo.querySelector('.product-form__quantity .quantity__input');
  }

  function getSubmitButton(productInfo) {
    return productInfo.querySelector('.bb-add-to-basket, .product-form__submit');
  }

  function findCartItem(cart, variantId) {
    return cart?.items?.find((item) => String(item.variant_id) === String(variantId));
  }

  function rememberNativeRules(input) {
    if (input.dataset.bbNativeRulesSaved === 'true') return;
    input.dataset.bbNativeRulesSaved = 'true';
    input.dataset.bbNativeMin = input.getAttribute('min') || input.dataset.min || '1';
    input.dataset.bbNativeDataMin = input.dataset.min || input.dataset.bbNativeMin;
    input.dataset.bbNativeMax = input.getAttribute('max') || '';
    input.dataset.bbNativeDataMax = input.dataset.max || '';
    input.dataset.bbAddQuantity = String(
      Math.max(1, parseInt(input.value, 10) || parseInt(input.dataset.bbNativeMin, 10) || 1)
    );
  }

  function setCartModeRules(input, quantity) {
    input.dataset.bbCartMode = 'true';
    input.classList.add('bb-product-cart-input');
    input.min = '0';
    input.dataset.min = '0';
    input.removeAttribute('max');
    delete input.dataset.max;
    input.value = String(quantity);
  }

  function restoreAddModeRules(input) {
    input.dataset.bbCartMode = 'false';
    input.classList.remove('bb-product-cart-input');

    const min = input.dataset.bbNativeMin || '1';
    const dataMin = input.dataset.bbNativeDataMin || min;
    input.setAttribute('min', min);
    input.dataset.min = dataMin;

    const max = input.dataset.bbNativeMax || '';
    const dataMax = input.dataset.bbNativeDataMax || '';
    if (max) input.setAttribute('max', max);
    else input.removeAttribute('max');
    if (dataMax) input.dataset.max = dataMax;
    else delete input.dataset.max;

    input.value = input.dataset.bbAddQuantity || min || '1';
  }

  function updateQuantityLabel(block, quantity) {
    const screenReaderLabel = block.querySelector('[id^="quantity-label-"]');
    if (screenReaderLabel) {
      screenReaderLabel.textContent = quantity > 0 ? `Quantity, ${quantity} in cart` : 'Quantity';
    }

    const visibleLabel = block.querySelector('.quantity__label > span[aria-hidden="true"]');
    if (visibleLabel) visibleLabel.textContent = 'Quantity';

    const cartRule = block.querySelector('.quantity__rules-cart');
    if (cartRule) {
      cartRule.textContent = quantity > 0 ? `(${quantity} in cart)` : '';
      cartRule.classList.toggle('hidden', quantity === 0);
    }
  }

  function applyCartState(productInfo, quantity, lineKey = '') {
    const input = getQuantityInput(productInfo);
    const block = input?.closest('.product-form__quantity');
    const submitButton = getSubmitButton(productInfo);
    const variantId = getVariantId(productInfo);
    if (!input || !block || !submitButton || !variantId) return;

    rememberNativeRules(input);

    const normalizedQuantity = Math.max(0, parseInt(quantity, 10) || 0);
    input.dataset.bbVariantId = String(variantId);
    input.dataset.bbCartQuantity = String(normalizedQuantity);
    input.dataset.cartQuantity = String(normalizedQuantity);
    input.dataset.bbLineKey = lineKey || '';

    if (normalizedQuantity > 0) {
      setCartModeRules(input, normalizedQuantity);
      block.classList.remove('hidden');
      submitButton.classList.add('hidden');
    } else {
      restoreAddModeRules(input);
      block.classList.add('hidden');
      submitButton.classList.remove('hidden');
    }

    updateQuantityLabel(block, normalizedQuantity);
    input.closest('quantity-input')?.validateQtyRules?.();
  }

  function patchNativeQuantityBoundaries() {
    const ProductInfo = customElements.get('product-info');
    if (!ProductInfo || ProductInfo.prototype.__bbCartQuantityPatch) return;

    const original = ProductInfo.prototype.setQuantityBoundries;
    if (typeof original !== 'function') return;

    ProductInfo.prototype.setQuantityBoundries = function () {
      const input = this.quantityInput || getQuantityInput(this);
      if (input?.dataset.bbCartMode === 'true') {
        const cartQuantity = Math.max(0, parseInt(input.dataset.bbCartQuantity || '0', 10) || 0);
        input.min = '0';
        input.dataset.min = '0';
        input.removeAttribute('max');
        delete input.dataset.max;
        input.value = String(cartQuantity);

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
        return;
      }

      return original.call(this);
    };

    ProductInfo.prototype.__bbCartQuantityPatch = true;
  }

  function initializeNativePatch() {
    patchNativeQuantityBoundaries();
    customElements.whenDefined?.('product-info').then(patchNativeQuantityBoundaries).catch(() => {});
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Unable to read cart (${response.status})`);
    return response.json();
  }

  function syncFromCart(cart, scope = document) {
    getProductInfos(scope).forEach((productInfo) => {
      const variantId = getVariantId(productInfo);
      if (!variantId) return;
      const item = findCartItem(cart, variantId);
      applyCartState(productInfo, item?.quantity || 0, item?.key || '');
    });
  }

  function updateCartBubbleCount(cart) {
    const target = document.getElementById('cart-icon-bubble');
    if (!target || !cart) return;

    const count = Math.max(0, parseInt(cart.item_count || '0', 10) || 0);
    let bubble = target.querySelector('.cart-count-bubble');

    if (count === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      target.appendChild(bubble);
    }

    let visibleCount = bubble.querySelector('span[aria-hidden="true"]');
    if (count < 100) {
      if (!visibleCount) {
        visibleCount = document.createElement('span');
        visibleCount.setAttribute('aria-hidden', 'true');
        bubble.prepend(visibleCount);
      }
      visibleCount.textContent = String(count);
    } else {
      visibleCount?.remove();
    }

    let accessibleCount = bubble.querySelector('.visually-hidden');
    if (!accessibleCount) {
      accessibleCount = document.createElement('span');
      accessibleCount.className = 'visually-hidden';
      bubble.appendChild(accessibleCount);
    }
    accessibleCount.textContent = count === 1 ? '1 item in cart' : `${count} items in cart`;
  }

  async function refreshFromCart(scope = document) {
    const cart = await fetchCart();
    syncFromCart(cart, scope);
    updateCartBubbleCount(cart);
    return cart;
  }

  async function handleQuantityChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.bbCartMode !== 'true') return;

    const productInfo = input.closest('product-info');
    if (!productInfo || input.dataset.bbBusy === 'true') return;

    const currentCartQuantity = Math.max(0, parseInt(input.dataset.bbCartQuantity || '0', 10) || 0);
    const desiredQuantity = Math.max(0, parseInt(input.value || '0', 10) || 0);
    const variantId = input.dataset.bbVariantId || getVariantId(productInfo);
    const quantityControl = input.closest('quantity-input');
    const buttons = Array.from(quantityControl?.querySelectorAll('button') || []);

    if (!variantId || desiredQuantity === currentCartQuantity) {
      input.value = String(currentCartQuantity);
      return;
    }

    input.dataset.bbBusy = 'true';
    input.readOnly = true;
    buttons.forEach((button) => (button.disabled = true));

    try {
      let lineKey = input.dataset.bbLineKey || '';
      if (!lineKey) {
        const cart = await fetchCart();
        const item = findCartItem(cart, variantId);
        lineKey = item?.key || '';
        if (!lineKey) {
          syncFromCart(cart, productInfo);
          updateCartBubbleCount(cart);
          return;
        }
      }

      const response = await fetch(routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: lineKey, quantity: desiredQuantity }),
      });

      const state = await response.json();
      if (!response.ok || state.errors) {
        throw new Error(state.description || state.message || 'Unable to update cart');
      }

      updateCartBubbleCount(state);
      syncFromCart(state, productInfo);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData: state,
          productVariantId: variantId,
        });
      }
    } catch (error) {
      console.error('[BuildingBasket] Product quantity update failed:', error);
      try {
        await refreshFromCart(productInfo);
      } catch {
        input.value = String(currentCartQuantity);
      }
    } finally {
      input.dataset.bbBusy = 'false';
      input.readOnly = false;
      buttons.forEach((button) => (button.disabled = false));
      quantityControl?.validateQtyRules?.();
    }
  }

  function handleExternalCartUpdate() {
    const now = Date.now();
    if (now - lastExternalSyncAt < 150) return Promise.resolve();
    lastExternalSyncAt = now;
    return refreshFromCart().catch(() => {});
  }

  function connectToCartUpdates() {
    if (cartSubscribed) return true;
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === SOURCE) return;
      return handleExternalCartUpdate();
    });

    if (PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        setTimeout(() => refreshFromCart().catch(() => {}), 0);
      });
    }

    cartSubscribed = true;
    return true;
  }

  function connectWithRetry(attempt = 0) {
    if (connectToCartUpdates() || attempt >= 40) return;
    window.setTimeout(() => connectWithRetry(attempt + 1), 50);
  }

  initializeNativePatch();
  refreshFromCart().catch(() => {});
  document.addEventListener('change', handleQuantityChange);
  document.addEventListener('buildingbasket:cart-updated', handleExternalCartUpdate);
  connectWithRetry();
})();
